import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, AlertCircle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { roleService } from '../../services/roleService';
import { supabase } from '../../lib/supabase';

// Import step components
import RoleCreationStep from './components/RoleCreationStep';
import PermissionAssignmentStep from './components/PermissionAssignmentStep';
import TeamMemberInvitationStep from './components/TeamMemberInvitationStep';
import ConfigurationReviewStep from './components/ConfigurationReviewStep';

export default function InitialSetupWizard() {
  const navigate = useNavigate();
  
  // Wizard state management
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    roles: [],
    permissions: {},
    teamMembers: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Step validation states
  const [stepValidation, setStepValidation] = useState({
    1: false, // Role creation
    2: false, // Permission assignment
    3: false, // Team member invitation
    4: false  // Review
  });

  // Define wizard steps
  const steps = [
    { number: 1, title: 'Create Roles', description: 'Define business roles and responsibilities' },
    { number: 2, title: 'Assign Permissions', description: 'Configure access levels for each role' },
    { number: 3, title: 'Invite Team Members', description: 'Add users and assign roles' },
    { number: 4, title: 'Review & Complete', description: 'Verify configuration and deploy' }
  ];

  // Load existing data if any
  useEffect(() => {
    loadExistingConfiguration();
  }, []);

  const loadExistingConfiguration = async () => {
    try {
      setLoading(true);
      
      // Check if setup is already completed
      const { data: existingRoles } = await supabase?.from('system_roles')?.select('*')?.eq('is_system_role', false);

      if (existingRoles && existingRoles?.length > 0) {
        // Setup already started or completed - load existing data
        const rolesWithPermissions = await Promise.all(
          existingRoles?.map(async (role) => {
            const permissions = await roleService?.getRolePermissions(role?.id);
            return {
              id: role?.id,
              name: role?.name,
              displayName: role?.display_name,
              description: role?.description,
              hierarchyLevel: role?.hierarchy_level,
              permissions: permissions
            };
          })
        );
        
        setWizardData(prev => ({
          ...prev,
          roles: rolesWithPermissions
        }));
      }
    } catch (err) {
      console.error('Error loading existing configuration:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers
  const goToNextStep = async () => {
    // Validate current step before proceeding
    const isValid = await validateCurrentStep();
    if (!isValid) {
      return;
    }

    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      setError('');
      setValidationErrors({});
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
      setValidationErrors({});
    }
  };

  const goToStep = (stepNumber) => {
    // Only allow navigation to completed or adjacent steps
    if (stepNumber <= currentStep + 1 && stepNumber >= 1) {
      setCurrentStep(stepNumber);
      setError('');
      setValidationErrors({});
    }
  };

  // Step validation
  const validateCurrentStep = async () => {
    setValidationErrors({});
    
    try {
      switch (currentStep) {
        case 1: // Role creation
          if (wizardData?.roles?.length === 0) {
            setValidationErrors({ roles: 'Please create at least one role before proceeding' });
            return false;
          }
          // Check if roles have required fields
          const invalidRoles = wizardData?.roles?.filter(r => !r?.name || !r?.displayName);
          if (invalidRoles?.length > 0) {
            setValidationErrors({ roles: 'All roles must have a name and display name' });
            return false;
          }
          break;

        case 2: // Permission assignment
          if (Object.keys(wizardData?.permissions)?.length === 0) {
            setValidationErrors({ permissions: 'Please assign permissions to at least one role' });
            return false;
          }
          // Validate that all roles have some permissions
          const rolesWithoutPermissions = wizardData?.roles?.filter(
            role => !wizardData?.permissions?.[role?.id] || 
                    Object.keys(wizardData?.permissions?.[role?.id])?.length === 0
          );
          if (rolesWithoutPermissions?.length > 0) {
            setValidationErrors({ 
              permissions: `The following roles need permissions: ${rolesWithoutPermissions?.map(r => r?.displayName)?.join(', ')}` 
            });
            return false;
          }
          break;

        case 3: // Team member invitation
          if (wizardData?.teamMembers?.length === 0) {
            setValidationErrors({ teamMembers: 'Please invite at least one team member' });
            return false;
          }
          // Validate email format and role assignment
          const invalidMembers = wizardData?.teamMembers?.filter(
            m => !m?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(m?.email) || !m?.roleId
          );
          if (invalidMembers?.length > 0) {
            setValidationErrors({ 
              teamMembers: 'All team members must have a valid email and assigned role' 
            });
            return false;
          }
          break;

        case 4: // Review - no additional validation needed
          break;
      }

      // Mark step as validated
      setStepValidation(prev => ({ ...prev, [currentStep]: true }));
      return true;
    } catch (err) {
      console.error('Validation error:', err);
      setValidationErrors({ general: err?.message });
      return false;
    }
  };

  // Update wizard data from child components
  const updateWizardData = (key, value) => {
    setWizardData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Complete setup and deploy configuration
  const completeSetup = async () => {
    try {
      setLoading(true);
      setError('');

      // Step 1: Create/update roles in database
      const createdRoleIds = [];
      for (const role of wizardData?.roles) {
        if (!role?.id) {
          // Create new role
          const newRole = await roleService?.createRole({
            name: role?.name,
            displayName: role?.displayName,
            description: role?.description,
            hierarchyLevel: role?.hierarchyLevel
          });
          createdRoleIds?.push({ tempId: role?.tempId, realId: newRole?.id });
        } else {
          createdRoleIds?.push({ tempId: role?.id, realId: role?.id });
        }
      }

      // Step 2: Assign permissions to roles
      for (const roleMapping of createdRoleIds) {
        const permissions = wizardData?.permissions?.[roleMapping?.tempId];
        if (permissions) {
          const permissionArray = Object.entries(permissions)?.map(([feature, accessLevel]) => ({
            feature,
            accessLevel
          }));
          await roleService?.updateRolePermissions(roleMapping?.realId, permissionArray);
        }
      }

      // Step 3: Invite team members and assign roles
      for (const member of wizardData?.teamMembers) {
        // Find real role ID
        const roleMapping = createdRoleIds?.find(r => r?.tempId === member?.roleId);
        const realRoleId = roleMapping?.realId || member?.roleId;

        // Create user profile (they'll receive email invitation)
        const { data: profile, error: profileError } = await supabase?.auth?.admin?.inviteUserByEmail(
          member?.email,
          {
            data: {
              first_name: member?.firstName || '',
              last_name: member?.lastName || '',
              department: member?.department || ''
            }
          }
        );

        if (profileError) throw profileError;

        // Assign role to user
        if (profile?.user?.id) {
          await roleService?.assignRoleToUser(profile?.user?.id, realRoleId);
        }
      }

      // Navigate to dashboard on successful completion
      navigate('/dashboard', { 
        state: { 
          setupCompleted: true,
          message: 'Initial setup completed successfully! Your CRM is ready to use.'
        }
      });
    } catch (err) {
      console.error('Error completing setup:', err);
      setError(err?.message || 'Failed to complete setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Render current step component
  const renderStepContent = () => {
    const stepProps = {
      wizardData,
      updateWizardData,
      validationErrors,
      setError
    };

    switch (currentStep) {
      case 1:
        return <RoleCreationStep {...stepProps} />;
      case 2:
        return <PermissionAssignmentStep {...stepProps} />;
      case 3:
        return <TeamMemberInvitationStep {...stepProps} />;
      case 4:
        return <ConfigurationReviewStep {...stepProps} onComplete={completeSetup} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Initial Setup Wizard</h1>
              <p className="mt-1 text-sm text-gray-500">
                Complete the guided setup to configure your CRM system
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Skip Setup
            </button>
          </div>
        </div>
      </div>
      {/* Progress Indicator */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => (
              <React.Fragment key={step?.number}>
                <div 
                  className={`flex items-center ${
                    step?.number <= currentStep ? 'cursor-pointer' : 'cursor-default'
                  }`}
                  onClick={() => step?.number <= currentStep && goToStep(step?.number)}
                >
                  {/* Step Circle */}
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center font-semibold
                      ${step?.number < currentStep 
                        ? 'bg-green-500 text-white' 
                        : step?.number === currentStep
                          ? 'bg-blue-600 text-white' :'bg-gray-200 text-gray-500'
                      }
                    `}>
                      {step?.number < currentStep ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : step?.number === currentStep ? (
                        <Circle className="w-6 h-6 fill-current" />
                      ) : (
                        <span>{step?.number}</span>
                      )}
                    </div>
                    
                    {/* Step Info */}
                    <div className="mt-3 text-center">
                      <p className={`text-sm font-medium ${
                        step?.number <= currentStep ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        {step?.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 max-w-xs">
                        {step?.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Connector Line */}
                {index < steps?.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    step?.number < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Validation Errors */}
        {Object.keys(validationErrors)?.length > 0 && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800">Please fix the following issues:</h3>
                <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                  {Object.values(validationErrors)?.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="mt-4 text-gray-600">Loading configuration...</p>
            </div>
          ) : (
            renderStepContent()
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={goToPreviousStep}
            disabled={currentStep === 1}
            className={`
              flex items-center px-6 py-3 rounded-lg font-medium
              ${currentStep === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }
            `}
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Previous
          </button>

          <div className="flex items-center space-x-4">
            {currentStep === 4 ? (
              <button
                onClick={completeSetup}
                disabled={loading}
                className="flex items-center px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Completing Setup...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Complete Setup
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={goToNextStep}
                className="flex items-center px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Next Step
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            )}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Step {currentStep} of {steps?.length} • {steps?.[currentStep - 1]?.description}
          </p>
          <button className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium">
            Need help? View setup guide →
          </button>
        </div>
      </div>
    </div>
  );
}